import {MapType} from '../../enums/map-type';
import {Logger} from '../../../common/utils/logger';
import {environment} from '../../../../environments/environment';
import type {MapCreditView} from './map-source-catalog';

/**
 * HERE's required copyright notice: '© <this year> HERE, <labels>', the
 * labels being the suppliers of the data in view -- national mapping
 * agencies for the street map, Maxar for the imagery. HERE publishes them
 * by style, area and zoom at GET /v3/copyright, and asks for the list to be
 * fetched again at least every 24 hours.
 *
 * The answer is the same for every user (and about 60 KB), so it is fetched
 * once per session, on the first credit asked for while HERE is on screen.
 */

const COPYRIGHT_URL = 'https://maps.hereapi.com/v3/copyright?apiKey=';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
/** After a failure, how long the fixed credit is shown before asking again. */
const RETRY_MS = 5 * 60 * 1000;
/** HERE's highest tile level (HereMapsTileProvider.getMaxZoom). */
const HERE_MAX_LEVEL = 20;

/**
 * The style each map type draws (HereMapsTileProvider). The night styles
 * credit the same suppliers as the day ones, so the day names stand for both.
 */
const STYLE_BY_MAP_TYPE: {[mapType: string]: string} = {
  [MapType.MAP]: 'explore.day',
  [MapType.SATELLITE]: 'satellite.day',
  [MapType.HYBRID]: 'explore.satellite.day',
  [MapType.TERRAIN]: 'topo.day',
};

interface HereBox {
  west: number;
  south: number;
  east: number;
  north: number;
}

interface HereCopyrightEntry {
  label?: string;
  minLevel?: number;
  maxLevel?: number;
  boundingBoxes?: HereBox[];
}

interface HereCopyrightAnswer {
  copyrights?: {[block: string]: HereCopyrightEntry[]};
  resources?: {[resource: string]: {styles?: {[style: string]: string[]}}};
}

let answer: HereCopyrightAnswer | null = null;
let fetchedAt = 0;
let failedAt = 0;
let pending = false;
let memo: {key: string, from: HereCopyrightAnswer, notice: string | null} | null = null;

function refresh() {
  const now = Date.now();
  if (pending || !environment.hereApiKey || now - failedAt < RETRY_MS) {
    return;
  }
  pending = true;
  fetch(COPYRIGHT_URL + encodeURIComponent(environment.hereApiKey))
    .then(response => response.ok ? response.json() : Promise.reject(new Error('HTTP ' + response.status)))
    .then((json: HereCopyrightAnswer) => {
      answer = json;
      fetchedAt = Date.now();
    })
    .catch(err => {
      failedAt = Date.now();
      Logger.warn('HERE copyright list unavailable', err);
    })
    .finally(() => {
      pending = false;
    });
}

/** Longitude spans of a box: two when it crosses the antimeridian. */
function spans(west: number, east: number): number[][] {
  return west <= east ? [[west, east]] : [[west, 180], [-180, east]];
}

function overlaps(box: HereBox, view: google.maps.LatLngBoundsLiteral): boolean {
  if (box.south > view.north || box.north < view.south) {
    return false;
  }
  // A box running "backwards" is HERE's way of writing the whole world
  // (west 180, east -180 in its own example); counted as covering it, as
  // crediting a supplier too many is the safe mistake.
  if (box.west > box.east) {
    return true;
  }
  return spans(view.west, view.east).some(([w, e]) => box.west <= e && box.east >= w);
}

/**
 * HERE's notice for `mapType` over `view`, or null until the list has
 * arrived (the catalog's fixed credit stands in meanwhile).
 */
export function hereCopyright(mapType: string, view: MapCreditView): string | null {
  if (!answer || Date.now() - fetchedAt > MAX_AGE_MS) {
    refresh();
  }
  const data = answer;
  if (!data) {
    return null;
  }
  const style = STYLE_BY_MAP_TYPE[mapType] || STYLE_BY_MAP_TYPE[MapType.MAP];
  // Beyond HERE's last level the map stretches that level's tiles
  // (HereMapsTileProvider.getMaxZoom), so its suppliers are still the ones.
  const zoom = Math.min(Math.round(view.zoom), HERE_MAX_LEVEL);
  const b = view.bounds;
  // Asked on every change-detection pass: worked out again only when the
  // view, the style or the list changed.
  const key = [style, zoom, b.west, b.south, b.east, b.north].join(',');
  if (memo && memo.key === key && memo.from === data) {
    return memo.notice;
  }
  const base = data.resources && data.resources['base'];
  const blocks = (base && base.styles && base.styles[style]) || [];
  const labels: string[] = [];
  blocks.forEach(block => ((data.copyrights && data.copyrights[block]) || []).forEach(entry => {
    const label = (entry.label || '').trim();
    // HERE itself already opens the notice.
    if (!label || /^here$/i.test(label) || labels.indexOf(label) !== -1
      || zoom < (entry.minLevel ?? 0) || zoom > (entry.maxLevel ?? 22)
      || !(entry.boundingBoxes || []).some(box => overlaps(box, b))) {
      return;
    }
    labels.push(label);
  }));
  // No supplier found -- an answer shaped other than expected, or an area it
  // lists nobody for -- leaves the catalog's fixed credit in place, which
  // names Maxar for the imagery: a bare '© HERE' would have dropped that
  // credit, and crediting a supplier too many is the safe mistake.
  const notice = labels.length ? [`© ${new Date().getFullYear()} HERE`, ...labels].join(', ') : null;
  memo = {key, from: data, notice};
  return notice;
}
